import { environment } from './environment';

/**
 * Runtime configuration — one bundle, many environments.
 *
 * Angular normally bakes `environment.ts` into the bundle at BUILD time, so moving to a new server
 * means rebuilding the whole frontend. That is fine for a flag like `production`, but wrong for
 * the API host: it turns "point the app at the new box" into a 15-minute build and a redeploy, and
 * it means the artifact you tested is not the artifact you ship.
 *
 * Instead `config.json` is fetched BEFORE the app bootstraps and merged into the same `environment`
 * object every service already imports. Because nothing in this codebase reads `environment.*` at
 * module scope — only inside constructors, which run after bootstrap — mutating that one object is
 * enough, and not one of the 46 files that import it has to change.
 *
 * Ops edits `config.json` on the server. No rebuild, no redeploy.
 */

/**
 * The keys `config.json` is allowed to set. An explicit allow-list on purpose: a typo like
 * "apiBaseURL" would otherwise silently create a new property, leave `apiBaseUrl` pointing at the
 * baked default, and send a production app at localhost with no error anywhere. Here it warns.
 */
const ALLOWED_KEYS = [
  'apiBaseUrl',
  'conversationBaseUrl',
  'wsBaseUrl',
  'eventApiBaseUrl',
  'publicApiBaseUrl',
  'appName',
  'defaultChannel',
] as const;

/**
 * Without these the app cannot reach its backend at all, so a missing one must be loud.
 *
 * These two only. The build already carries correct values for every URL, so config.json is an
 * OVERRIDE mechanism, not the source of truth — a blank one leaves the baked values in place and
 * the app runs exactly as it does today. Requiring all four made sense when the values were blank
 * and a wrong host was worse than a refusal to boot; on a server whose baked values are proven,
 * that same rule turns a working deploy into a "Configuration error" screen for every user.
 *
 * These two stay listed so that an explicitly BLANKED build still fails loudly rather than
 * silently calling a relative URL that resolves to the SPA's own index.html.
 */
const REQUIRED_KEYS = [
  'apiBaseUrl',
  'eventApiBaseUrl',
] as const;

/** Where ops puts the file. Resolved against <base href> so a sub-path deployment still works. */
const CONFIG_PATH = 'config.json';

function fatal(message: string, detail: string): never {
  // The app never boots, so there is no Angular error handler and no toast — write plain DOM.
  // A blank white page with a console error is the worst possible failure for an ops team at
  // 2am; this tells them the file to fix and the URL that failed.
  const el = document.createElement('div');
  el.setAttribute('style', [
    'position:fixed', 'inset:0', 'z-index:99999',
    'display:flex', 'align-items:center', 'justify-content:center',
    'padding:24px', 'background:#ffffff', 'color:#1b2019',
    "font-family:system-ui,-apple-system,'Segoe UI',sans-serif",
  ].join(';'));
  el.innerHTML =
    '<div style="max-width:640px">' +
    '<div style="font-size:18px;font-weight:800;margin-bottom:8px">Configuration error</div>' +
    '<div style="font-size:14px;line-height:1.6;margin-bottom:12px">' + message + '</div>' +
    '<pre style="font-size:12px;background:#eff2ec;padding:12px;border-radius:8px;' +
    'white-space:pre-wrap;word-break:break-all;margin:0">' + detail + '</pre></div>';
  document.body.appendChild(el);
  throw new Error('[runtime-config] ' + message + ' — ' + detail);
}

/**
 * Loads `config.json` and merges it into `environment`. Call and await BEFORE
 * `bootstrapApplication`.
 *
 * In a production build a missing or malformed file is FATAL — booting on the baked
 * `http://localhost:8081` would look like a broken backend and send everyone hunting the wrong
 * problem. In a development build it falls back to the baked values with a warning, so
 * `ng serve` works with no extra setup.
 */
export async function loadRuntimeConfig(): Promise<void> {
  const url = new URL(CONFIG_PATH, document.baseURI).href;
  // Cache-buster + no-store: a stale config.json cached by the browser or a proxy would survive
  // the very redeploy that was meant to change it.
  const bustedUrl = url + (url.includes('?') ? '&' : '?') + 'v=' + Date.now();

  let raw: unknown;
  try {
    const res = await fetch(bustedUrl, { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) throw new Error('HTTP ' + res.status + ' ' + res.statusText);
    raw = await res.json();
  } catch (err) {
    const detail = url + '\n' + String(err);
    if (environment.production) {
      fatal('The app could not load its runtime configuration file. It must be served at the '
            + 'URL below and be valid JSON.', detail);
    }
    console.warn('[runtime-config] using built-in defaults —', detail);
    return;
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    const detail = url + '\nExpected a JSON object, got: ' + JSON.stringify(raw);
    if (environment.production) fatal('The runtime configuration file is not a JSON object.', detail);
    console.warn('[runtime-config] ignoring malformed config —', detail);
    return;
  }

  const cfg = raw as Record<string, unknown>;
  const applied: string[] = [];
  const unknown: string[] = [];

  for (const key of Object.keys(cfg)) {
    // JSON has no comments, so an underscore prefix is the convention this file uses for notes to
    // whoever edits it on the server. Skipped silently — warning about them would train ops to
    // ignore the warning that actually matters (a mistyped key).
    if (key.startsWith('_')) continue;
    if ((ALLOWED_KEYS as readonly string[]).includes(key)) {
      const value = cfg[key];
      // Only strings are meaningful here; a null or number is a mistake worth ignoring loudly
      // rather than assigning and breaking a URL join somewhere far away.
      if (typeof value === 'string' && value.trim() !== '') {
        (environment as Record<string, unknown>)[key] = value.trim();
        applied.push(key);
      } else if (typeof value === 'string') {
        // An empty string is the SHIPPED state of every URL in config.json — deliberately blank so
        // an unedited file cannot satisfy the required-key check. Leaving it unset here is the
        // whole mechanism, so it is not a warning; the REQUIRED_KEYS check below is what speaks.
      } else {
        unknown.push(key + ' (ignored: not a string)');
      }
    } else {
      unknown.push(key);
    }
  }

  const missing = REQUIRED_KEYS.filter(
    (k) => typeof (environment as Record<string, unknown>)[k] !== 'string'
        || !((environment as Record<string, unknown>)[k] as string).trim());
  if (missing.length) {
    const detail = url + '\nMissing or empty: ' + missing.join(', ')
                 + '\nAllowed keys: ' + ALLOWED_KEYS.join(', ');
    if (environment.production) fatal('The runtime configuration is missing a required setting.', detail);
    console.warn('[runtime-config]', detail);
  }

  if (unknown.length) {
    // Never fatal — an unknown key is usually a typo, and refusing to boot over one would be
    // worse than running. But it is exactly the bug that is invisible otherwise, so it is loud.
    console.warn('[runtime-config] ignored unrecognised key(s) in config.json:', unknown.join(', '),
                 '\n  Recognised keys are:', ALLOWED_KEYS.join(', '));
  }
  if (applied.length) {
    console.info('[runtime-config] applied from config.json:', applied.join(', '));
  }
}
